import { useDepartments } from "@/composables/useDepartments";
import { useEntityVisibility } from "@/composables/useEntityVisibility";
import { useExternalDepartments } from "@/composables/useExternalDepartments";
import { useExternalGroups } from "@/composables/useExternalGroups";
import { useGroups } from "@/composables/useGroups";
import { IS_BP_MANAGER } from "@/consts";
import converters from "@/converters";
import {
  Group_Connection,
  Module_Type,
  type CompactDepartmentFragment,
  type CompactGroupFragment,
} from "@/graphql/generated-types";
import whenComposablesReady from "@/helpers/requests/whenComposablesReady";
import type { ListResourceType, VisibleStateTypes } from "@/types";

export type SharingFeatureState = {
  id?: number;
  departments: string[];
  externalDepartments: string[];
  groups: string[];
  externalGroups: string[];
  parentGroups: string[];
  externalContentVisibility?: VisibleStateTypes;
  original?: Partial<SharingFeatureState>;
  updateGroupPosts: boolean;
};

export type DatabaseModel = {
  departments?: Array<
    { __typename?: "Department" } & CompactDepartmentFragment
  > | null;
  groups?: Array<{ __typename?: "Group" } & CompactGroupFragment> | null;
};

export type SetStateProps = {
  data: DatabaseModel;
  state: SharingFeatureState;
  moduleType: Module_Type;
  groupConnection?: Group_Connection;
  listResourceType?: ListResourceType;
  onCompleted: () => void;
};

export type SharingInput = {
  departments?: string[];
  groups?: string[];
};

type GetItemOptions = {
  useDepartments?: boolean;
  useGroups?: boolean;
};

const DEFUALT_GET_ITEM_OPTIONS: GetItemOptions = {
  useDepartments: true,
  useGroups: true,
};

const toSharedOptions = (store: SharingFeatureState): SharingInput => {
  // BP Manager has only one department. We shouldn't send anything. BE handles it
  if (IS_BP_MANAGER) {
    return {
      departments: undefined,
      groups: undefined,
    };
  }

  return {
    departments: [
      ...(store.departments || []),
      ...(store.externalDepartments || []),
    ],
    groups: [
      ...(store.groups || []),
      ...(store.externalGroups || []),
      ...(store.parentGroups || []),
    ],
  };
};

export const SHARING_FEATURE = {
  getDefault(): SharingFeatureState {
    return {
      departments: [],
      externalDepartments: [],
      groups: [],
      externalGroups: [],
      parentGroups: [],
      externalContentVisibility: undefined,
      updateGroupPosts: true,
    };
  },

  hash(state: SharingFeatureState) {
    return {
      externalContentVisibility: state.externalContentVisibility,
    };
  },

  setState(props: SetStateProps) {
    const {
      data,
      state,
      moduleType,
      groupConnection,
      listResourceType,
      onCompleted,
    } = props;
    // We should wait the list to be loaded to select departments. Because API
    // returns departments and external departments in the same array. We don't
    // know which id is related with which list. That causes a race condition
    // issue.
    const departments = useDepartments();
    const externalDepartments = useExternalDepartments(moduleType);
    const groups = useGroups(moduleType);

    const externalGroups = groupConnection
      ? useExternalGroups(groupConnection)
      : undefined;

    let entityVisibility = undefined;

    if (listResourceType === "external") {
      entityVisibility = useEntityVisibility(moduleType);
      // No need to await here. whenComposablesReady() function will handle it
      entityVisibility.load();
    }

    // We don't know when the composable data will be loaded. But we can not
    // wait to select an item because it effects lots of things such as
    // spinners, warnings, etc. Because of the race condition issue, setting
    // the following data should be wait a little. This will be occurred only
    // when the user refresh the page on activities. Once the data is ready,
    // users can't notice any difference.
    whenComposablesReady(
      [
        departments,
        externalDepartments,
        groups,
        externalDepartments,
        entityVisibility,
      ],
      () => {
        state.departments = converters.shared.toSelectableIds(
          departments.departmentList,
          data.departments,
        );
        state.externalDepartments = converters.shared.toSelectableIds(
          externalDepartments.externalDepartmentList,
          data.departments,
        );

        state.groups = converters.shared.toSelectableIds(
          groups.groupList,
          data.groups,
        );

        if (externalGroups) {
          state.externalGroups = converters.shared.toSelectableIds(
            externalGroups.externalGroupList,
            data.groups,
          );
        }

        state.parentGroups = converters.shared.toSelectableIds(
          groups.parentGroupList,
          data.groups,
        );

        if (entityVisibility) {
          state.externalContentVisibility = entityVisibility.isVisible(
            state.id,
          );
        }

        if (!state.original) {
          state.original = {};
        }

        // The original group data should be set. We need that information to
        // show a confirmation dialog to user.
        // https://proreact.atlassian.net/browse/IBG-9508
        state.original.groups = [...state.groups];
        state.original.externalGroups = [...state.externalGroups];
        state.original.parentGroups = [...state.parentGroups];

        onCompleted();
      },
    );
  },

  getItem(
    state: SharingFeatureState,
    options: GetItemOptions = DEFUALT_GET_ITEM_OPTIONS,
  ) {
    const { departments, groups } = toSharedOptions(state);

    const allGroups = [
      ...(state.original?.groups || []),
      ...(state.original?.parentGroups || []),
      ...(state.original?.externalGroups || []),
    ];

    return {
      departments: options.useDepartments ? departments : undefined,
      groups: options.useGroups ? groups : undefined,
      updateGroupPosts: allGroups.length ? state.updateGroupPosts : undefined,
    };
  },

  onAfterSave(state: SharingFeatureState) {
    if (state.original) {
      state.original.groups = [...state.groups];
      state.original.externalGroups = [...state.externalGroups];
      state.original.parentGroups = [...state.parentGroups];
    }
  },

  isSharedWithGroups(state: SharingFeatureState) {
    if (!state.original) {
      return false;
    }

    return (
      [
        ...(state.original.groups || []),
        ...(state.original.externalGroups || []),
        ...(state.original.parentGroups || []),
      ].length > 0
    );
  },
};
